import React, { useState, useEffect } from 'react';
import { getTarotReading, TarotReadingResult, TarotCardPositionReading } from '../services/geminiService';
import { TAROT_SPREADS, TarotSpreadConfig } from '../data/tarotPreloadedReadings';
import { PhoneIcon, CardsIcon, RefreshCwIcon, LockIcon } from './Icons';
import { useClient } from '../contexts/ClientContext';

export const MAJOR_ARCANA = [
    "Le Mat", "Le Bateleur", "La Papesse", "L'Impératrice", "L'Empereur", 
    "Le Pape", "L'Amoureux", "Le Chariot", "La Justice", "L'Ermite", 
    "La Roue de Fortune", "La Force", "Le Pendu", "La Mort", "Tempérance", 
    "Le Diable", "La Maison Dieu", "L'Étoile", "La Lune", "Le Soleil", "Le Jugement", "Le Monde"
];

// URLs des images RWS (Rider-Waite-Smith) sur Wikimedia Commons
export const TAROT_IMAGES: Record<string, string> = {
    "Le Mat": "https://upload.wikimedia.org/wikipedia/commons/9/90/RWS_Tarot_00_Fool.jpg",
    "Le Bateleur": "https://upload.wikimedia.org/wikipedia/commons/d/de/RWS_Tarot_01_Magician.jpg",
    "La Papesse": "https://upload.wikimedia.org/wikipedia/commons/8/88/RWS_Tarot_02_High_Priestess.jpg",
    "L'Impératrice": "https://upload.wikimedia.org/wikipedia/commons/d/d2/RWS_Tarot_03_Empress.jpg",
    "L'Empereur": "https://upload.wikimedia.org/wikipedia/commons/c/c3/RWS_Tarot_04_Emperor.jpg",
    "Le Pape": "https://upload.wikimedia.org/wikipedia/commons/8/8d/RWS_Tarot_05_Hierophant.jpg",
    "L'Amoureux": "https://upload.wikimedia.org/wikipedia/commons/d/db/RWS_Tarot_06_Lovers.jpg",
    "Le Chariot": "https://upload.wikimedia.org/wikipedia/commons/9/9b/RWS_Tarot_07_Chariot.jpg",
    "La Justice": "https://upload.wikimedia.org/wikipedia/commons/e/e0/RWS_Tarot_11_Justice.jpg",
    "L'Ermite": "https://upload.wikimedia.org/wikipedia/commons/4/4d/RWS_Tarot_09_Hermit.jpg",
    "La Roue de Fortune": "https://upload.wikimedia.org/wikipedia/commons/3/3c/RWS_Tarot_10_Wheel_of_Fortune.jpg",
    "La Force": "https://upload.wikimedia.org/wikipedia/commons/f/f5/RWS_Tarot_08_Strength.jpg",
    "Le Pendu": "https://upload.wikimedia.org/wikipedia/commons/2/2b/RWS_Tarot_12_Hanged_Man.jpg",
    "La Mort": "https://upload.wikimedia.org/wikipedia/commons/d/d7/RWS_Tarot_13_Death.jpg",
    "L'Arcane sans nom": "https://upload.wikimedia.org/wikipedia/commons/d/d7/RWS_Tarot_13_Death.jpg",
    "Tempérance": "https://upload.wikimedia.org/wikipedia/commons/f/f8/RWS_Tarot_14_Temperance.jpg",
    "Le Diable": "https://upload.wikimedia.org/wikipedia/commons/5/55/RWS_Tarot_15_Devil.jpg",
    "La Maison Dieu": "https://upload.wikimedia.org/wikipedia/commons/5/53/RWS_Tarot_16_Tower.jpg",
    "L'Étoile": "https://upload.wikimedia.org/wikipedia/commons/d/db/RWS_Tarot_17_Star.jpg",
    "La Lune": "https://upload.wikimedia.org/wikipedia/commons/7/7f/RWS_Tarot_18_Moon.jpg",
    "Le Soleil": "https://upload.wikimedia.org/wikipedia/commons/1/17/RWS_Tarot_19_Sun.jpg",
    "Le Jugement": "https://upload.wikimedia.org/wikipedia/commons/d/dd/RWS_Tarot_20_Judgement.jpg",
    "Le Monde": "https://upload.wikimedia.org/wikipedia/commons/f/ff/RWS_Tarot_21_World.jpg"
};

const TarotOracle: React.FC = () => {
    const { client, recordDivinationActivity } = useClient();
    const [selectedSpreadId, setSelectedSpreadId] = useState<'single' | 'three' | 'cross' | 'horseshoe' | 'astrological'>('three');
    const [question, setQuestion] = useState('');
    const [drawnCards, setDrawnCards] = useState<string[]>([]);
    const [reading, setReading] = useState<TarotReadingResult | null>(null);
    const [step, setStep] = useState<'input' | 'shuffling' | 'dealing' | 'flipping' | 'result'>('input');
    const [shufflePhase, setShufflePhase] = useState(0);
    const [isLoadingReading, setIsLoadingReading] = useState(false);
    const [selectedCardDetail, setSelectedCardDetail] = useState<number | null>(null);

    const currentSpreadConfig: TarotSpreadConfig = TAROT_SPREADS.find(s => s.id === selectedSpreadId) || TAROT_SPREADS[1];

    const handleStart = (e: React.FormEvent) => {
        e.preventDefault();
        setStep('shuffling');
        setShufflePhase(0);
        setSelectedCardDetail(null);

        setTimeout(() => setShufflePhase(1), 1700);
        setTimeout(() => setShufflePhase(2), 3400);
        
        // 5.0 secondes de mélange mystique
        setTimeout(() => {
            drawCardsLogic();
        }, 5000);
    };

    const drawCardsLogic = async () => {
        setStep('dealing');
        setIsLoadingReading(true);
        
        const count = currentSpreadConfig.cardCount;
        const shuffled = [...MAJOR_ARCANA].sort(() => 0.5 - Math.random());
        const selected = shuffled.slice(0, count);
        setDrawnCards(selected);

        const apiPromise = getTarotReading(question, selected, selectedSpreadId);

        setTimeout(async () => {
            setStep('flipping');
            
            try {
                const result = await apiPromise;
                setReading(result);
                if (client) {
                    recordDivinationActivity(
                        'tarot', 
                        `Tarot : ${currentSpreadConfig.name}`, 
                        `Question : "${question || 'Tirage Général'}" - Cartes : ${selected.join(', ')}`, 
                        { spreadId: selectedSpreadId, question, drawnCards: selected, reading: result }
                    );
                }
            } catch (e) {
                console.warn("Erreur tirage tarot:", e);
            } finally {
                setIsLoadingReading(false);
                setStep('result');
            }
        }, 1200);
    };

    const reset = () => {
        setQuestion('');
        setDrawnCards([]);
        setReading(null);
        setStep('input');
        setIsLoadingReading(false);
        setSelectedCardDetail(null);
    };

    // Dos de carte AstroX Premium & Responsive
    const CardBack = () => (
        <div className="w-full h-full bg-gradient-to-br from-[#1b0d2d] via-[#12071f] to-[#0a0312] rounded-xl sm:rounded-2xl border border-gold-500/50 sm:border-2 sm:border-gold-500/60 flex flex-col items-center justify-center shadow-xl relative overflow-hidden p-1.5 sm:p-3 select-none">
            <div className="absolute top-1.5 left-1.5 sm:top-2 sm:left-2 w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 border-t-2 border-l-2 border-gold-400/60"></div>
            <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 border-t-2 border-r-2 border-gold-400/60"></div>
            <div className="absolute bottom-1.5 left-1.5 sm:bottom-2 sm:left-2 w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 border-b-2 border-l-2 border-gold-400/60"></div>
            <div className="absolute bottom-1.5 right-1.5 sm:bottom-2 sm:right-2 w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 border-b-2 border-r-2 border-gold-400/60"></div>
            
            <div className="w-full h-full opacity-15 bg-[radial-gradient(#fbbf24_1px,transparent_1px)] [background-size:12px_12px] absolute inset-0 pointer-events-none"></div>
            <div className="absolute inset-1 sm:inset-1.5 border border-purple-500/30 rounded-lg sm:rounded-xl"></div>
            
            <div className="relative z-10 flex flex-col items-center justify-center text-center">
                <div className="w-6 h-6 sm:w-9 sm:h-9 rounded-full bg-purple-950/80 border border-gold-400/50 flex items-center justify-center shadow-[0_0_12px_rgba(251,191,36,0.3)] mb-0.5 sm:mb-1.5">
                    <CardsIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gold-400" />
                </div>
                <h3 className="text-gold-300 font-serif font-bold text-[10px] sm:text-sm tracking-wider uppercase leading-tight">
                    Astro<span className="text-purple-300">X</span>
                </h3>
                <span className="text-[6px] sm:text-[8px] text-purple-300/80 uppercase tracking-[0.2em] mt-0.5">Oracle</span>
            </div>
        </div>
    );

    // Composant Carte Individuelle avec Flip 3D
    const TarotCardItem = ({ card, index, customWidth }: { card: string; index: number; customWidth?: string }) => {
        const posInfo = currentSpreadConfig.positionNames[index] || { title: `Lame ${index + 1}`, role: '' };
        const isFlipped = step === 'result' || step === 'flipping';
        const isSelected = selectedCardDetail === index;

        return (
            <div 
                onClick={() => {
                    if (step === 'result') {
                        setSelectedCardDetail(prev => prev === index ? null : index);
                    }
                }}
                className={`relative aspect-[2/3.3] sm:aspect-[2/3.2] transition-all duration-700 cursor-pointer ${
                    customWidth || 'w-full max-w-[160px]'
                } ${step === 'dealing' ? 'opacity-0 translate-y-12' : 'opacity-100 translate-y-0'} ${
                    isSelected ? 'ring-4 ring-gold-400 rounded-2xl scale-105 z-20 shadow-[0_0_30px_rgba(251,191,36,0.6)]' : 'hover:scale-102'
                }`}
                style={{ transitionDelay: `${index * 80}ms` }}
            >
                <div 
                    className={`relative w-full h-full duration-700 transform-style-3d transition-transform ${isFlipped ? 'rotate-y-180' : ''}`}
                    style={{ transitionDelay: `${index * 100 + 150}ms` }}
                >
                    {/* DOS */}
                    <div className="absolute inset-0 backface-hidden">
                        <CardBack />
                    </div>

                    {/* FACE */}
                    <div className="absolute inset-0 backface-hidden rotate-y-180">
                        <div className="w-full h-full rounded-xl sm:rounded-2xl overflow-hidden shadow-[0_0_25px_rgba(251,191,36,0.3)] border sm:border-2 border-gold-400 bg-black flex flex-col relative group">
                            <img 
                                src={TAROT_IMAGES[card]} 
                                alt={card}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                    (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                                }}
                            />
                            {/* Fallback Image */}
                            <div className="hidden absolute inset-0 flex flex-col items-center justify-center bg-purple-950 text-gold-300 p-2 text-center">
                                <CardsIcon className="w-8 h-8 text-gold-400 mb-2" />
                                <span className="font-serif font-bold text-xs sm:text-sm">{card}</span>
                            </div>
                            
                            {/* Bandeau Titre & Position */}
                            <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black via-black/90 to-transparent pt-4 pb-1.5 px-1 text-center">
                                <span className="text-gold-400 text-[8px] sm:text-[10px] uppercase font-bold tracking-wider block truncate">
                                    {posInfo.title}
                                </span>
                                <span className="text-white font-serif font-semibold text-[10px] sm:text-xs truncate block leading-tight">
                                    {card}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <section className="py-8 sm:py-14 md:py-18 bg-transparent min-h-screen relative overflow-hidden flex items-center justify-center">
            
            {/* Fond d'ambiance cosmique et mystique */}
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_50%_40%,_rgba(67,44,122,0.25)_0%,_transparent_70%)]"></div>
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-72 sm:w-96 h-72 sm:h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none"></div>

            <div className="container mx-auto px-3 sm:px-4 relative z-10 w-full max-w-5xl text-center">
                
                {/* En-tête Titre Épuré */}
                <div className={`transition-all duration-700 ${step !== 'input' ? 'mb-4' : 'mb-6'}`}>
                    <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-purple-900/50 border border-purple-500/40 text-gold-400 text-xs font-semibold uppercase tracking-widest mb-3 backdrop-blur-md">
                        <span>Oracle de Marseille</span>
                    </div>
                    <h1 className="text-3xl sm:text-5xl md:text-6xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-300 via-white to-purple-300 drop-shadow-lg">
                        L'Oracle du Tarot
                    </h1>
                </div>

                {/* --- ÉTAPE 1 : BOUTONS COMPACTS & QUESTION --- */}
                {step === 'input' && (
                    <div className="max-w-3xl mx-auto bg-black/60 backdrop-blur-2xl border border-purple-500/40 rounded-3xl p-5 sm:p-8 md:p-10 shadow-[0_0_50px_rgba(139,92,246,0.2)] transition-all">
                        
                        {/* BOUTONS COMPACTS DE SÉLECTION (SANS EMOJIS, AVEC NOMBRE DE CARTES) */}
                        <div className="mb-6">
                            <label className="text-xs font-bold text-gray-300 uppercase tracking-widest block text-left mb-3">
                                Sélectionnez votre Tirage :
                            </label>

                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-2.5">
                                {TAROT_SPREADS.map((spread) => {
                                    const isSelected = selectedSpreadId === spread.id;
                                    return (
                                        <button
                                            key={spread.id}
                                            type="button"
                                            onClick={() => setSelectedSpreadId(spread.id)}
                                            className={`py-3 px-2 sm:px-3 rounded-2xl transition-all duration-200 border text-center flex flex-col items-center justify-center gap-1 cursor-pointer select-none active:scale-95 ${
                                                isSelected
                                                    ? 'bg-gradient-to-r from-amber-500 via-gold-400 to-amber-500 text-black border-gold-300 font-extrabold shadow-[0_0_20px_rgba(251,191,36,0.45)] ring-2 ring-gold-400/50 scale-102'
                                                    : 'bg-gray-900/80 hover:bg-gray-800 text-gray-300 hover:text-white border-purple-500/30'
                                            }`}
                                        >
                                            <span className="text-xs sm:text-sm font-bold tracking-tight block">
                                                {spread.cardCount} {spread.cardCount === 1 ? 'Carte' : 'Cartes'}
                                            </span>
                                            <span className={`text-[10px] sm:text-[11px] leading-tight block ${
                                                isSelected ? 'text-black/80 font-semibold' : 'text-purple-300/80'
                                            }`}>
                                                ({spread.id === 'single' ? 'Flash' : spread.id === 'three' ? 'Passé / Présent / Futur' : spread.id === 'cross' ? 'En Croix' : spread.id === 'horseshoe' ? 'Fer à Cheval' : 'Astrologique'})
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* QUESTION DU CONSULTANT */}
                        <form onSubmit={handleStart} className="space-y-6">
                            <div className="space-y-2 text-left">
                                <label className="text-xs font-bold text-gray-300 uppercase tracking-widest block">
                                    Votre Question (Optionnel) :
                                </label>
                                <div className="relative">
                                    <input 
                                        type="text" 
                                        value={question}
                                        onChange={(e) => setQuestion(e.target.value)}
                                        placeholder={`Ex: Que me révèlent les cartes (${currentSpreadConfig.cardCount} cartes) ?`}
                                        className="w-full bg-purple-950/50 border border-purple-500/40 rounded-2xl px-4 py-3.5 text-white placeholder-purple-300/40 focus:outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-400/30 text-sm sm:text-base transition-all shadow-inner"
                                    />
                                </div>
                            </div>

                            {/* BOUTON SPECULAR CTA */}
                            <button 
                                type="submit"
                                className="w-full relative overflow-hidden bg-gradient-to-r from-gold-500 via-amber-400 to-gold-500 hover:from-gold-400 hover:to-gold-600 text-black font-black py-4 px-6 rounded-2xl shadow-[0_0_30px_rgba(251,191,36,0.4)] transition-all active:scale-[0.98] hover:scale-[1.01] uppercase tracking-widest text-xs sm:text-sm flex items-center justify-center gap-2 cursor-pointer specular-shine"
                            >
                                <CardsIcon className="w-5 h-5 text-black" />
                                <span>Tirer {currentSpreadConfig.cardCount} {currentSpreadConfig.cardCount === 1 ? 'Arcane' : 'Arcanes'} ({currentSpreadConfig.shortName})</span>
                            </button>
                        </form>
                    </div>
                )}

                {/* --- ZONE DE JEU & ANIMATIONS --- */}
                {step !== 'input' && (
                    <div className="relative min-h-[420px] sm:min-h-[500px] flex flex-col items-center justify-center w-full">
                        
                        {/* ANIMATION DE MÉLANGE 3D (5 SECONDES) */}
                        {step === 'shuffling' && (
                            <div className="relative flex flex-col items-center justify-center w-full py-8 sm:py-12 select-none">
                                <div className="absolute w-56 h-56 sm:w-80 sm:h-80 rounded-full bg-gradient-to-tr from-purple-600/20 via-gold-500/20 to-indigo-600/20 blur-2xl animate-pulse pointer-events-none"></div>
                                <div className="absolute w-48 h-48 sm:w-64 sm:h-64 rounded-full border border-gold-400/30 animate-spin-slow pointer-events-none"></div>
                                
                                <div className="relative w-32 h-48 sm:w-44 sm:h-64 flex items-center justify-center my-6">
                                    {[
                                        { rotate: '-32deg', translate: '-50px', anim: 'animate-card-fan-1' },
                                        { rotate: '-16deg', translate: '-25px', anim: 'animate-card-fan-2' },
                                        { rotate: '0deg',   translate: '0px',   anim: 'animate-card-fan-center' },
                                        { rotate: '16deg',  translate: '25px',  anim: 'animate-card-fan-4' },
                                        { rotate: '32deg',  translate: '50px',  anim: 'animate-card-fan-5' },
                                    ].map((c, idx) => (
                                        <div 
                                            key={idx}
                                            className={`absolute inset-0 rounded-xl sm:rounded-2xl transition-all duration-700 shadow-[0_8px_25px_rgba(0,0,0,0.6)] ${c.anim}`}
                                            style={{ zIndex: idx === 2 ? 10 : 5 }}
                                        >
                                            <CardBack />
                                        </div>
                                    ))}
                                </div>

                                <div className="relative z-10 text-center mt-4">
                                    <p className="text-gold-300 font-serif italic text-base sm:text-xl tracking-wide drop-shadow animate-pulse">
                                        {shufflePhase === 0 && "Mélange des Arcanes de Marseille..."}
                                        {shufflePhase === 1 && "Alignement des énergies..."}
                                        {shufflePhase === 2 && `Sélection des ${currentSpreadConfig.cardCount} cartes...`}
                                    </p>
                                    
                                    <div className="w-36 sm:w-48 h-1 bg-purple-950/80 rounded-full mx-auto mt-4 overflow-hidden border border-purple-500/30">
                                        <div className="h-full bg-gradient-to-r from-gold-400 via-amber-300 to-purple-400 animate-shimmer-progress"></div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* --- PLATEAU DE RÉVÉLATION SPÉCIFIQUE ET CLAIR POUR CHAQUE TIRAGE --- */}
                        {(step === 'dealing' || step === 'flipping' || step === 'result') && (
                            <div className="w-full">
                                
                                {/* 1. TIRAGE 1 CARTE (FLASH) */}
                                {drawnCards.length === 1 && (
                                    <div className="flex justify-center items-center my-6">
                                        <TarotCardItem card={drawnCards[0]} index={0} customWidth="w-[180px] sm:w-[220px] md:w-[260px]" />
                                    </div>
                                )}

                                {/* 2. TIRAGE 3 CARTES (PASSÉ / PRÉSENT / FUTUR) */}
                                {drawnCards.length === 3 && (
                                    <div className="grid grid-cols-3 gap-3 sm:gap-6 md:gap-8 max-w-2xl mx-auto my-6 px-2">
                                        {drawnCards.map((card, i) => (
                                            <TarotCardItem key={i} card={card} index={i} customWidth="w-full" />
                                        ))}
                                    </div>
                                )}

                                {/* 3. TIRAGE EN CROIX (5 CARTES) */}
                                {drawnCards.length === 5 && (
                                    <div className="max-w-2xl mx-auto my-6 px-2">
                                        <div className="grid grid-cols-3 gap-2 sm:gap-4 items-center">
                                            {/* Ligne 1 : En Haut (Évolution - index 2) */}
                                            <div></div>
                                            <div className="flex justify-center">
                                                <TarotCardItem card={drawnCards[2]} index={2} customWidth="w-28 sm:w-36" />
                                            </div>
                                            <div></div>

                                            {/* Ligne 2 : Gauche (Pour - index 0) | Centre (Synthèse - index 4) | Droite (Contre - index 1) */}
                                            <div className="flex justify-center">
                                                <TarotCardItem card={drawnCards[0]} index={0} customWidth="w-28 sm:w-36" />
                                            </div>
                                            <div className="flex justify-center">
                                                <TarotCardItem card={drawnCards[4]} index={4} customWidth="w-28 sm:w-36" />
                                            </div>
                                            <div className="flex justify-center">
                                                <TarotCardItem card={drawnCards[1]} index={1} customWidth="w-28 sm:w-36" />
                                            </div>

                                            {/* Ligne 3 : En Bas (Réponse - index 3) */}
                                            <div></div>
                                            <div className="flex justify-center">
                                                <TarotCardItem card={drawnCards[3]} index={3} customWidth="w-28 sm:w-36" />
                                            </div>
                                            <div></div>
                                        </div>
                                    </div>
                                )}

                                {/* 4. TIRAGE FER À CHEVAL (7 CARTES EN 2 RANGÉES CLAIRES) */}
                                {drawnCards.length === 7 && (
                                    <div className="max-w-3xl mx-auto my-6 space-y-4 px-2">
                                        {/* Rangée supérieure (4 cartes) */}
                                        <div className="grid grid-cols-4 gap-2 sm:gap-4">
                                            {drawnCards.slice(0, 4).map((card, i) => (
                                                <TarotCardItem key={i} card={card} index={i} customWidth="w-full" />
                                            ))}
                                        </div>
                                        {/* Rangée inférieure (3 cartes centrées) */}
                                        <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-xl mx-auto">
                                            {drawnCards.slice(4, 7).map((card, i) => (
                                                <TarotCardItem key={i + 4} card={card} index={i + 4} customWidth="w-full" />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* 5. TIRAGE ASTROLOGIQUE (13 CARTES BIEN ESPACÉES EN 3 RANGÉES DE 4 + 1 SYNTHÈSE AU CENTRE) */}
                                {drawnCards.length >= 12 && (
                                    <div className="max-w-4xl mx-auto my-6 space-y-4 px-2">
                                        <div className="text-center mb-2">
                                            <span className="text-[11px] font-bold text-gold-400 uppercase tracking-widest bg-purple-950/80 px-3 py-1 rounded-full border border-purple-500/30">
                                                Les 12 Maisons Célestes & La Synthèse Astrale
                                            </span>
                                        </div>
                                        
                                        {/* Maisons 1 à 4 */}
                                        <div className="grid grid-cols-4 gap-2 sm:gap-3">
                                            {drawnCards.slice(0, 4).map((card, i) => (
                                                <TarotCardItem key={i} card={card} index={i} customWidth="w-full" />
                                            ))}
                                        </div>

                                        {/* Maisons 5 à 8 */}
                                        <div className="grid grid-cols-4 gap-2 sm:gap-3">
                                            {drawnCards.slice(4, 8).map((card, i) => (
                                                <TarotCardItem key={i + 4} card={card} index={i + 4} customWidth="w-full" />
                                            ))}
                                        </div>

                                        {/* Maisons 9 à 12 */}
                                        <div className="grid grid-cols-4 gap-2 sm:gap-3">
                                            {drawnCards.slice(8, 12).map((card, i) => (
                                                <TarotCardItem key={i + 8} card={card} index={i + 8} customWidth="w-full" />
                                            ))}
                                        </div>

                                        {/* 13ème Carte : Synthèse Globale (Centrée & Mise en valeur) */}
                                        {drawnCards[12] && (
                                            <div className="pt-2 flex flex-col items-center">
                                                <span className="text-[10px] font-bold text-gold-300 uppercase tracking-wider mb-1">
                                                    Arcane XIII : Synthèse Globale
                                                </span>
                                                <TarotCardItem card={drawnCards[12]} index={12} customWidth="w-32 sm:w-40" />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* ÉTAT DE CHARGEMENT */}
                                {isLoadingReading && (
                                    <div className="max-w-md mx-auto bg-black/80 backdrop-blur-xl border border-gold-500/40 rounded-2xl p-6 shadow-2xl text-center animate-fade-in mb-6">
                                        <div className="w-10 h-10 border-2 border-gold-400 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                                        <p className="text-gold-300 font-serif text-base sm:text-lg font-bold">
                                            Révélation du {currentSpreadConfig.name}...
                                        </p>
                                        <p className="text-purple-200/70 text-xs mt-1">
                                            L'Oracle analyse vos {currentSpreadConfig.cardCount} cartes.
                                        </p>
                                    </div>
                                )}

                                {/* --- RÉVÉLATION & GUIDANCE CLAIREMENT AFFICHÉE --- */}
                                {step === 'result' && reading && (
                                    <div className="max-w-5xl mx-auto bg-black/80 backdrop-blur-2xl border border-gold-500/40 rounded-3xl p-5 sm:p-8 md:p-10 shadow-[0_0_70px_rgba(139,92,246,0.25)] animate-fade-in-up text-left relative overflow-hidden mt-6">
                                        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-transparent via-gold-400 to-transparent"></div>
                                        
                                        <div className="text-center mb-8 border-b border-purple-500/30 pb-4">
                                            <span className="text-xs font-bold text-gold-400 uppercase tracking-widest block mb-1">
                                                Révélation des Arcanes & Guidance de l'Oracle
                                            </span>
                                            <h3 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-white">
                                                {currentSpreadConfig.name}
                                            </h3>
                                            {question && (
                                                <p className="text-purple-200/80 text-xs sm:text-sm mt-1.5 italic">
                                                    Question posée : « {question} »
                                                </p>
                                            )}
                                        </div>
                                        
                                        {/* GRILLE DÉTAILLÉE DE TOUTES LES POSITIONS ET ARCANES */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mb-8">
                                            {(reading.positions || []).map((pos, idx) => {
                                                const isHighlighted = selectedCardDetail === idx;
                                                return (
                                                    <div 
                                                        key={idx}
                                                        id={`pos-card-${idx}`}
                                                        className={`p-4 sm:p-5 rounded-2xl border transition-all duration-300 ${
                                                            isHighlighted
                                                                ? 'bg-purple-900/80 border-gold-400 shadow-[0_0_25px_rgba(251,191,36,0.4)] scale-102 ring-2 ring-gold-400'
                                                                : 'bg-purple-950/40 hover:bg-purple-950/60 border-purple-500/30 shadow-md hover:border-gold-400/40'
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between gap-2 mb-2 pb-1.5 border-b border-purple-500/20">
                                                            <span className="text-[11px] font-bold uppercase tracking-wider text-gold-300 flex items-center gap-1.5">
                                                                <span className="w-1.5 h-1.5 rounded-full bg-gold-400"></span>
                                                                {pos.positionTitle}
                                                            </span>
                                                            <span className="text-[10px] font-mono font-bold text-purple-200 bg-purple-900/80 px-2 py-0.5 rounded-full border border-purple-500/30">
                                                                {pos.cardName}
                                                            </span>
                                                        </div>
                                                        <p className="text-gray-200 text-xs sm:text-sm leading-relaxed whitespace-pre-line">
                                                            {pos.text}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* CONSEIL SACRÉ & SYNTHÈSE DE L'ORACLE */}
                                        <div className="space-y-4 mb-8">
                                            <div className="bg-gradient-to-r from-purple-950/80 via-indigo-950/80 to-purple-950/80 p-5 sm:p-6 rounded-2xl border border-gold-500/40 shadow-xl">
                                                <h4 className="text-gold-300 font-bold mb-2 text-sm sm:text-base font-serif">
                                                    Conseil de l'Oracle & Guidance Directe
                                                </h4>
                                                <p className="text-gray-100 text-xs sm:text-sm leading-relaxed pl-3 border-l-2 border-gold-400 whitespace-pre-line">
                                                    {reading.advice}
                                                </p>
                                            </div>

                                            <div className="bg-gradient-to-r from-amber-950/40 via-purple-950/40 to-black/70 p-5 sm:p-6 rounded-2xl border border-purple-500/30">
                                                <h4 className="text-purple-200 font-bold mb-2 text-sm sm:text-base font-serif">
                                                    Synthèse Globale du Tirage
                                                </h4>
                                                <p className="text-gray-200 text-xs sm:text-sm leading-relaxed whitespace-pre-line">
                                                    {reading.synthesis}
                                                </p>
                                            </div>
                                        </div>

                                        {/* BOUTONS D'ACTION */}
                                        <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 pt-2">
                                            <button 
                                                onClick={reset} 
                                                className="w-full sm:w-auto px-6 py-3.5 border border-purple-400/40 text-gray-200 hover:text-white rounded-xl hover:bg-purple-900/30 transition-all uppercase tracking-wider text-xs font-semibold cursor-pointer flex items-center justify-center gap-2 shadow-sm active:scale-95"
                                            >
                                                <RefreshCwIcon className="w-4 h-4" /> Autre Tirage
                                            </button>
                                            <a 
                                                href="tel:0985405274" 
                                                className="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white font-bold rounded-xl shadow-[0_0_20px_rgba(16,185,129,0.4)] hover:scale-[1.02] transition-transform flex items-center justify-center gap-2 uppercase tracking-wider text-xs cursor-pointer"
                                            >
                                                <PhoneIcon className="w-4 h-4" /> Approfondir en direct (09 85 40 52 74)
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                
                {/* CSS ANIMATIONS */}
                <style>{`
                    .perspective-1000 { perspective: 1000px; }
                    .transform-style-3d { transform-style: preserve-3d; }
                    .backface-hidden { backface-visibility: hidden; }
                    .rotate-y-180 { transform: rotateY(180deg); }
                    
                    .animate-spin-slow {
                        animation: spinSlow 20s linear infinite;
                    }

                    .animate-shimmer-progress {
                        animation: shimmerBar 1.6s ease-in-out infinite;
                    }

                    .animate-card-fan-1 { animation: fanCard1 2.2s cubic-bezier(0.4, 0, 0.2, 1) infinite; }
                    .animate-card-fan-2 { animation: fanCard2 2.2s cubic-bezier(0.4, 0, 0.2, 1) infinite; }
                    .animate-card-fan-center { animation: fanCardCenter 2.2s cubic-bezier(0.4, 0, 0.2, 1) infinite; }
                    .animate-card-fan-4 { animation: fanCard4 2.2s cubic-bezier(0.4, 0, 0.2, 1) infinite; }
                    .animate-card-fan-5 { animation: fanCard5 2.2s cubic-bezier(0.4, 0, 0.2, 1) infinite; }

                    @keyframes spinSlow {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }

                    @keyframes shimmerBar {
                        0% { width: 10%; opacity: 0.5; }
                        50% { width: 85%; opacity: 1; }
                        100% { width: 100%; opacity: 0.6; }
                    }

                    @keyframes fanCard1 {
                        0%, 100% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
                        45% { transform: translate3d(-52px, 8px, 0) rotate(-28deg) scale(1.03); }
                        65% { transform: translate3d(-30px, 4px, 0) rotate(-14deg) scale(1.02); }
                    }

                    @keyframes fanCard2 {
                        0%, 100% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
                        45% { transform: translate3d(-26px, 2px, 0) rotate(-14deg) scale(1.04); }
                        65% { transform: translate3d(-15px, 1px, 0) rotate(-7deg) scale(1.02); }
                    }

                    @keyframes fanCardCenter {
                        0%, 100% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
                        45% { transform: translate3d(0, -12px, 10px) rotate(0deg) scale(1.08); filter: drop-shadow(0 0 16px rgba(251,191,36,0.6)); }
                    }

                    @keyframes fanCard4 {
                        0%, 100% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
                        45% { transform: translate3d(26px, 2px, 0) rotate(14deg) scale(1.04); }
                        65% { transform: translate3d(15px, 1px, 0) rotate(7deg) scale(1.02); }
                    }

                    @keyframes fanCard5 {
                        0%, 100% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
                        45% { transform: translate3d(52px, 8px, 0) rotate(28deg) scale(1.03); }
                        65% { transform: translate3d(30px, 4px, 0) rotate(14deg) scale(1.02); }
                    }
                `}</style>
            </div>
        </section>
    );
};

export default TarotOracle;
